/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from "react-i18next";
import PIHeader from "./partials/PIHeader";
import {
	PhyInspCondition,
	PhyInspContainer,
} from "./platePhysicalInspection.types";
import { useEffect, useMemo, useState } from "react";
import { postAutoclaveServices } from "../../../../service/postAutoclaveServices";
import PIStatCard from "./partials/PIStatCard";
import MediaPrepTable from "../../../common/commonComponents/lotDetailsTable";
import { getColumnsForPhyInspContainers } from "./partials/columns";
import PIFooter from "./partials/PIFooter";
import PIConfirmModal from "./partials/PIConfirmModal";
import CustomToast from "../../../../widgets/toaster";
import {
	PAMediaBatch,
	PAMediaRemovalBatchListResponse,
} from "../mediaRemoval/mediaRemoval.types";

const PlatePhysicalInspection = () => {
	const { t } = useTranslation();
	const [mediaBatches, setMediaBatches] = useState<PAMediaBatch[]>([]);
	const [selectedBatch, setSelectedBatch] = useState<PAMediaBatch>();
	const [containers, setContainers] = useState<PhyInspContainer[]>([]);
	const [totalContainers, setTotalContainers] = useState<number>(0);
	const [inspectionCondtions, setInspectionConditions] = useState<
		PhyInspCondition[]
	>([]);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(false);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});
	const batchInfo = {
		id: selectedBatch?.media_batch_identifier,
		mediaName: selectedBatch?.media_type,
		activityName: selectedBatch?.intended_use_name ?? "",
		totalPlates: totalContainers,
	};
	const [stats, setStats] = useState({
		inspected: 0,
		passed: 0,
		rejected: 0,
		pending: 0,
		progress: 0,
		allInspected: false,
	});

	const [errors, setErrors] = useState({
	batch: "",
	table: "",
	containers: {} as Record<string, string>,
	});

	const validate = () => {
  		const newErrors = {
			batch: "",
			table: "",
			containers: {} as Record<string, string>,
		};

		let isValid = true;

		if (!selectedBatch) {
			newErrors.batch = t("postAutoclaveProcess.errors.batch");
			isValid = false;
		}

		if (containers.length === 0) {
			newErrors.table = t("postAutoclaveProcess.errors.plates");
			isValid = false;
		}

		containers.forEach((container) => {
			// Inspection condition is mandatory
			if (!container.inspection_reason_id) {
			newErrors.containers[container.container_id] =
				t("postAutoclaveProcess.errors.inspectionCondition");
			isValid = false;
			}

			// Remarks required only for rejected containers
			if (
			container.rejected &&
			!container.rejection_reason?.trim()
			) {
			newErrors.containers[container.container_id] =
				t("postAutoclaveProcess.errors.rejectionReason");
			isValid = false;
			}
		});

		setErrors(newErrors);
		return isValid;
	};

		
	const handleConfirm = () => {
	if (!validate()) return;

	setShowConfirmationModal(true);
	};

	const getMediaBatchList = async () => {
		try {
			const urlParams = {};
			const response: PAMediaRemovalBatchListResponse =
				await postAutoclaveServices.getMediaBatches(urlParams);
			setMediaBatches(response.data);
			setSelectedBatch(undefined);
		} catch (error) {
			console.error(error);
		}
	};

	const getContainersForInspection = async () => {
		if (!selectedBatch) return;
		const urlParams = {
			sort: "id",
			order: "desc",
			keyword: searchQuery,
			media_batch_id: selectedBatch.id,
		};
		try {
			const response =
				await postAutoclaveServices.getContainersForInspection(urlParams);
			setContainers(response.data);
			if (!response.statistics) {
				setTotalContainers(0);
				setStats({
					inspected: 0,
					passed: 0,
					rejected: 0,
					pending: 0,
					progress: 0,
					allInspected: false,
				});

				return;
			}

			const totalPlates = Number(response.statistics.total_plates);
			const passed = Number(response.statistics.passed);
			const rejected = Number(response.statistics.failed);
			const inspected = passed + rejected;
			const pending = totalPlates - inspected;

			setTotalContainers(totalPlates);

			setStats({
				inspected,
				passed,
				rejected,
				pending,
				progress: totalPlates > 0 ? (inspected / totalPlates) * 100 : 0,
				allInspected: pending === 0 && totalPlates > 0,
			});
		} catch (error) {
			console.log(error);
			setContainers([]);
			setTotalContainers(0);
			setStats({
				inspected: 0,
				passed: 0,
				rejected: 0,
				pending: 0,
				progress: 0,
				allInspected: false,
			});
		}
	};

	const getInspectionConditions = async () => {
		const urlParams = {};
		try {
			const response =
				await postAutoclaveServices.getContainerInspectionConditions(
					urlParams,
				);
			setInspectionConditions(response.data);
		} catch (error) {
			console.log(error);
		}
	};

	const handleConditionChange = (
		row: PhyInspContainer,
		condition_id: string,
	) => {
		setErrors(prev => ({
			...prev,
			containers: {
			...prev.containers,
			[row.container_id]: "",
			},
		}));
		

		const selectedCondition = inspectionCondtions.find(
			(cond) => cond.id === condition_id,
		);
		const isRejected = selectedCondition?.is_reject === "t";

		setContainers((prev) =>
			prev.map((cont) =>
				cont.container_id === row.container_id
					? {
							...cont,
							inspection_reason_id: Number(condition_id),
							rejected: isRejected,
							rejection_reason: isRejected ? cont.rejection_reason : "", // clear reason if not rejected
						}
					: cont,
			),
		);
	};

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const handleReasonChange = (row: PhyInspContainer, reason: string) => {
		setErrors((prev) => ({
			...prev,
			containers: {
				...prev.containers,
				[row.container_id]: "",
			},
		}));

		setContainers((prev) =>
			prev.map((cont) =>
				cont.container_id === row.container_id
					? { ...cont, rejection_reason: reason }
					: cont,
			),
		);
	};

	const refreshData = () => {
		setRefreshTrigger((prev) => !prev);
	};

	const currentStats = useMemo(() => {
		const passed = containers.filter(
			(c) => c.rejected === false
		).length;

		const rejected = containers.filter(
			(c) => c.rejected === true
		).length;

		const inspected = passed + rejected;
		const pending = totalContainers - inspected;

		return {
			...stats,
			inspected,
			passed,
			rejected,
			pending,
			progress:
				totalContainers > 0
					? (inspected / totalContainers) * 100
					: 0,
			allInspected: pending === 0 && totalContainers > 0,
		};
	}, [containers, totalContainers]);

	useEffect(() => {
		getInspectionConditions();
		getMediaBatchList();
	}, []);

	useEffect(() => {
		setContainers([]);
		setTotalContainers(0);
		getContainersForInspection();
	}, [searchQuery, refreshTrigger, selectedBatch]);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("postAutoclaveProcess.title")} pageDescription={t('pageDescription.postAutoclave.description')} />
			<div className="flex flex-col h-full bg-[#F8FAFC] relative overflow-hidden">
				<PIHeader
					mediaBatches={mediaBatches}
					selectedBatch={selectedBatch}
					setSelectedBatch={setSelectedBatch}
					errors={errors}
					setErrors={setErrors}
    				batchInfo={batchInfo}
				/>
				<div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
					<div className="grid grid-cols-3 gap-6">
						<PIStatCard
							title={t(
								"postAutoclaveProcess.physicalInspection.platesInspected",
							)}
							value={`${currentStats.inspected} / ${totalContainers}`}
							icon="analytics"
							titleColor="text-slate-400"
							valueColor="text-[#1E293B]"
							iconBgColor="bg-indigo-50"
							iconColor="text-indigo-500"
							progress={currentStats.progress}
						/>

						<PIStatCard
							title={t(
								"postAutoclaveProcess.physicalInspection.platesPassed",
							)}
							value={currentStats.passed}
							icon="check_circle"
							titleColor="text-emerald-400"
							valueColor="text-emerald-600"
							iconBgColor="bg-emerald-50"
							iconColor="text-emerald-500"
							subtitle="Verified Reusable"
						/>

						<PIStatCard
							title={t(
								"postAutoclaveProcess.physicalInspection.platesRejected",
							)}
							value={currentStats.rejected}
							icon="cancel"
							titleColor="text-red-400"
							valueColor="text-red-600"
							iconBgColor="bg-red-50"
							iconColor="text-red-500"
							subtitle="Permanently Rejected"
						/>
					</div>
					<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[250px]">
						<div className="px-8 py-2 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
							<div className="flex items-center gap-4 flex-1 max-w-md">
								<div className="relative w-full group">
									<span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
										qr_code_scanner
									</span>
									<input
										type="text"
										placeholder={t(
											"postAutoclaveProcess.physicalInspection.searchPlaceholder",
										)}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="w-full pl-12 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 transition-all"
									/>
								</div>
							</div>

							<div className="flex items-center gap-4">
								{/* <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
									<button className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg bg-white text-[#1E293B] shadow-sm border-none">{t("postAutoclaveProcess.physicalInspection.allPlates",)}</button>
									<button className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg text-slate-400 border-none">{t("postAutoclaveProcess.physicalInspection.pending",)}</button>
								</div> */}
							</div>
						</div>
						<MediaPrepTable
							columns={getColumnsForPhyInspContainers(
								t,
								inspectionCondtions,
								handleConditionChange,
								handleReasonChange,
								errors
							)}
							data={containers}
							emptyMessage={t(
								"postAutoclaveProcess.physicalInspection.noPlatesFound",
							)}
						/>
						{errors.table && (
							<div className="px-8 py-2">
								<p className="text-[11px] text-red-500">
									{errors.table}
								</p>
							</div>
						)}
					</div>
				</div>
				<PIFooter
					stats={currentStats}
					containers={containers}
					showConfirmationModal={handleConfirm}
				/>
				{showConfirmationModal && (
					<PIConfirmModal
						showToast={showToast}
						refreshData={refreshData}
						stats={currentStats}
						containers={containers}
						mediaBatchId={selectedBatch?.id ?? ""}
						closeConfirmationModal={() => setShowConfirmationModal(false)}
					/>
				)}
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={400}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default PlatePhysicalInspection;
